export type RefreshArea = 'customers' | 'customer' | 'actions' | 'dashboard' | 'orders'
type Event = { name: string; payload?: { operation?: unknown; changes?: import('./liveRefreshQueue').WorkspaceChange[]; customerIds?: string[]; orderIds?: number[] } }

/** Reconnect snapshots have no operation and must refresh all visible data. */
export function affectsWorkspace(event: Event | null, area: RefreshArea): boolean {
  if (!event) return false
  if (event.name !== 'WorkspaceChanged') {
    if (event.name === 'OrderPlaced') return area === 'orders' || area === 'customer'
    if (['ActionAdded', 'ActionClosed', 'ActionClosedWithNew', 'ActionDeleted'].includes(event.name)) return area !== 'orders'
    return false
  }
  if (event.payload?.changes) return event.payload.changes.some(change => affectsWorkspace({ name: event.name, payload: change }, area))
  const operation = event.payload?.operation
  if (typeof operation !== 'string' || !operation) return true
  const subjects: Record<RefreshArea, RegExp> = {
    customers: /Customer|Contact|Action|User/,
    customer: /Customer|Contact|Note|Action|Order/,
    actions: /Action|Customer|User/,
    dashboard: /Action|Customer|User/,
    orders: /Order|Customer|Form/,
  }
  return subjects[area].test(operation)
}

/** Only the newest request may replace a screen's current data. */
export function latestRequest() {
  let revision = 0
  return () => { const mine = ++revision; return () => mine === revision }
}

export function affectsRecord(event: Event | null, area: RefreshArea, kind: 'customer' | 'order', id: string | number): boolean {
  if (!event || !affectsWorkspace(event, area)) return false
  if (event.payload?.changes) return event.payload.changes.some(change => affectsRecord({ name: event.name, payload: change }, area, kind, id))
  const ids = kind === 'customer' ? event.payload?.customerIds : event.payload?.orderIds
  return !ids?.length || ids.some(value => String(value).toLowerCase() === String(id).toLowerCase())
}

/** null means a full list catch-up is required, otherwise fetch only these orders. */
export function changedOrderIds(event: Event | null): number[] | null {
  if (!event || event.name !== 'WorkspaceChanged') return null
  const changes = event.payload?.changes ?? [event.payload ?? {}]
  const relevant = changes.filter(change => affectsWorkspace({ name: 'WorkspaceChanged', payload: change }, 'orders'))
  if (relevant.some(change => !change.orderIds?.length)) return null
  return [...new Set(relevant.flatMap(change => change.orderIds ?? []))]
}
